class CmpFitmentConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const rawFitments = Array.isArray(window.CMP_FITMENT_DATA) ? window.CMP_FITMENT_DATA : [];
    this.fitments = rawFitments.flatMap((item) => {
      const displayModels = String(item.model || '')
        .split(/\s+\/\s+|\r?\n+/)
        .map((model) => model.trim())
        .filter(Boolean);

      return displayModels.map((model) => ({
        ...item,
        model,
        filterModel: item.filterModel || model,
        coupler: this.normalizeCoupler(item.coupler),
      }));
    });
    this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
    this.hideNative = this.dataset.hideNativeFilters === 'true';
    this.form = this.querySelector('.cmp-fitment-configurator__form');
    this.makeSelect = this.querySelector('[data-cmp-fitment-make]');
    this.modelSelect = this.querySelector('[data-cmp-fitment-model]');
    this.couplerSelect = this.querySelector('[data-cmp-fitment-coupler]');
    this.submitButton = this.querySelector('[data-cmp-fitment-submit]');
    this.clearButton = this.querySelector('[data-cmp-fitment-clear]');
    this.status = this.querySelector('[data-cmp-fitment-status]');
    this.params = {
      make: 'filter.p.m.custom.make',
      model: 'filter.p.m.custom.model',
      coupler: 'filter.p.m.custom.coupler_system',
    };
    this.displayModelParam = 'cmp_model';

    if (!this.fitments.length || !this.form) {
      this.hidden = true;
      return;
    }

    this.populateSelect(this.makeSelect, this.unique(this.fitments.map((item) => item.make)), 'Select make');
    this.restoreFromUrl();

    this.makeSelect.addEventListener('change', () => this.onMakeChange());
    this.modelSelect.addEventListener('change', () => this.onModelChange());
    this.couplerSelect.addEventListener('change', () => this.updateSubmitState());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.clearButton.addEventListener('click', () => this.clearFitment());

    if (this.hideNative) {
      this.hideNativeFitmentFilters();
      const nativeFilters = document.getElementById('main-collection-filters');
      if (nativeFilters) {
        this.nativeObserver = new MutationObserver(() => this.hideNativeFitmentFilters());
        this.nativeObserver.observe(nativeFilters, { childList: true, subtree: true });
      }
    }
  }

  disconnectedCallback() {
    if (this.nativeObserver) this.nativeObserver.disconnect();
  }

  unique(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  normalizeCoupler(value) {
    const normalized = String(value || '').replace(/\s*\/\s*/g, '/').trim();
    return normalized === 'Pin On' || normalized === 'Pin Grabber' ? 'Pin On/Pin Grabber' : normalized;
  }

  populateSelect(select, values, placeholder, selectedValue = '') {
    select.replaceChildren();
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      option.selected = value === selectedValue;
      select.appendChild(option);
    });

    select.disabled = values.length === 0;
  }

  restoreFromUrl() {
    const search = new URLSearchParams(window.location.search);
    const make = search.get(this.params.make) || '';
    const filterModel = search.get(this.params.model) || '';
    const rawCoupler = search.get(this.params.coupler) || '';
    const coupler = this.normalizeCoupler(rawCoupler);
    const requestedDisplayModel = search.get(this.displayModelParam) || '';
    const displayFitment = this.fitments.find(
      (item) =>
        item.make === make &&
        item.model === requestedDisplayModel &&
        (!coupler || item.coupler === coupler) &&
        Boolean(requestedDisplayModel)
    );
    const filterFitment = this.fitments.find(
      (item) => item.make === make && item.filterModel === filterModel && (!coupler || item.coupler === coupler)
    );
    const matchingFitment = displayFitment || filterFitment;
    const displayModel = matchingFitment?.model || requestedDisplayModel || filterModel;
    const needsModelRepair = Boolean(matchingFitment && filterModel !== matchingFitment.filterModel);

    if ((rawCoupler && rawCoupler !== coupler) || needsModelRepair) {
      if (matchingFitment) search.set(this.params.model, matchingFitment.filterModel);
      search.set(this.params.coupler, coupler);
      if (displayModel) search.set(this.displayModelParam, displayModel);
      search.delete('page');
      window.location.assign(`${this.collectionUrl}?${search.toString()}`);
      return;
    }

    if (make && this.fitments.some((item) => item.make === make)) {
      this.makeSelect.value = make;
      this.populateModels(displayModel);
      if (this.modelSelect.value) this.populateCouplers(coupler);
    }

    this.updateSubmitState();
  }

  onMakeChange() {
    this.populateModels('');
    this.updateSubmitState();
  }

  populateModels(selectedValue) {
    const make = this.makeSelect.value;
    const models = this.unique(this.fitments.filter((item) => item.make === make).map((item) => item.model));
    this.populateSelect(this.modelSelect, models, make ? 'Select model' : 'Select make first', selectedValue);
    this.populateSelect(this.couplerSelect, [], 'Select model first');
  }

  onModelChange() {
    this.populateCouplers('');
    this.updateSubmitState();
  }

  populateCouplers(selectedValue) {
    const make = this.makeSelect.value;
    const model = this.modelSelect.value;
    const couplers = this.unique(
      this.fitments.filter((item) => item.make === make && item.model === model).map((item) => item.coupler)
    );
    this.populateSelect(this.couplerSelect, couplers, model ? 'Select coupler' : 'Select model first', selectedValue);
    if (!selectedValue && couplers.length === 1) this.couplerSelect.value = couplers[0];
    this.updateSubmitState();
  }

  updateSubmitState() {
    const ready = Boolean(this.makeSelect.value && this.modelSelect.value && this.couplerSelect.value);
    this.submitButton.disabled = !ready;
  }

  onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.disabled) return;

    const make = this.makeSelect.value;
    const model = this.modelSelect.value;
    const coupler = this.normalizeCoupler(this.couplerSelect.value);
    const fitment = this.fitments.find(
      (item) => item.make === make && item.model === model && item.coupler === coupler
    );
    if (!fitment) return;

    const search = new URLSearchParams(window.location.search);
    Object.values(this.params).forEach((param) => search.delete(param));
    search.delete('page');
    search.set(this.params.make, make);
    search.set(this.params.model, fitment.filterModel);
    search.set(this.params.coupler, coupler);
    search.set(this.displayModelParam, model);
    this.status.textContent = `Loading attachments for ${make} ${model}.`;
    window.location.assign(`${this.collectionUrl}?${search.toString()}`);
  }

  clearFitment() {
    const search = new URLSearchParams(window.location.search);
    Object.values(this.params).forEach((param) => search.delete(param));
    search.delete(this.displayModelParam);
    search.delete('page');
    const query = search.toString();
    window.location.assign(query ? `${this.collectionUrl}?${query}` : this.collectionUrl);
  }

  hideNativeFitmentFilters() {
    Object.values(this.params).forEach((param) => {
      document.querySelectorAll(`input[name="${param}"]`).forEach((input) => {
        const filter = input.closest('.js-filter');
        if (filter) filter.hidden = true;
      });
    });
    this.preserveActiveFitmentParams();
  }

  preserveActiveFitmentParams() {
    const search = new URLSearchParams(window.location.search);
    const preservedParams = [...Object.values(this.params), this.displayModelParam];

    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      preservedParams.forEach((param) => {
        const value = search.get(param);
        const existingHidden = form.querySelector(`input[data-cmp-fitment-preserve][name="${param}"]`);
        const hasCheckedValue = Array.from(form.querySelectorAll(`input[name="${param}"]`)).some(
          (input) => !input.hasAttribute('data-cmp-fitment-preserve') && input.checked && input.value === value
        );

        if (!value || hasCheckedValue) {
          if (existingHidden) existingHidden.remove();
          return;
        }

        if (existingHidden) {
          if (existingHidden.value !== value) existingHidden.value = value;
          return;
        }

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = param;
        hidden.value = value;
        hidden.setAttribute('data-cmp-fitment-preserve', '');
        form.appendChild(hidden);
      });
    });
  }
}

if (!customElements.get('cmp-fitment-configurator')) {
  customElements.define('cmp-fitment-configurator', CmpFitmentConfigurator);
}
